import { SubmitHandler } from 'react-hook-form';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Separator } from '@givebutter/primitives/separator';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { updateCampaign } from 'api/campaigns';
import { fetchFundById, fetchFunds } from 'api/funds';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import { FUNDS_PARAMS } from 'constants/params';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface FundSettingsValues {
    showFund: boolean;
    defaultFund: string;
}

const FUND_NONE_VALUE = '0';

const CampaignToolsFundSettings = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.fundsWithFilters(accountId, FUNDS_PARAMS),
        queryFn: () => fetchFunds(accountId, FUNDS_PARAMS),
    });

    const { data: defaultFund } = useSuspenseQuery({
        queryKey: accountQueryKeys.fundById(accountId, campaign.defaultFund?.value),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchFundById(accountId, Number(campaign.defaultFund?.value)),
                !!campaign.defaultFund?.value,
            ),
    });

    const funds = data.data;

    const methods = Form.useGivebutterForm<FundSettingsValues>({
        defaultValues: {
            showFund: !campaign?.hideFund?.active,
            defaultFund: defaultFund ? defaultFund.id.toString() : FUND_NONE_VALUE,
        },
    });

    const { control, path, handleSubmit } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: (values: FundSettingsValues) => {
            const useDefaultFund = parseInt(values.defaultFund) > 0;
            const data = {
                settings: [
                    {
                        code: Settings.HideFund.Code,
                        active: !values.showFund,
                    },
                    {
                        code: Settings.DefaultFund.Code,
                        value: useDefaultFund ? values.defaultFund : null,
                        active: useDefaultFund,
                    },
                ],
            };

            return updateCampaign({ campaignId: campaign?.id!, data });
        },
        onSuccess: () => toast.success('Updated fund settings!'),
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to update fund settings'),
    });

    return (
        <Form.Provider {...methods}>
            <SettingsCard.Root>
                <SettingsCard.LeftSide>
                    <SettingsCard.Heading>Fund settings</SettingsCard.Heading>
                    <p className={'text-sm text-gray-700'}>
                        Manage your campaign level fund configurations.
                    </p>
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={
                            'https://help.givebutter.com/en/articles/4534181-how-to-manage-funds-donation-designations'
                        }
                        className={'text-sm'}
                    >
                        Read the guide
                    </a>
                    .
                </SettingsCard.LeftSide>
                <SettingsCard.RightSide>
                    <form onSubmit={handleSubmit(submit as SubmitHandler<FundSettingsValues>)}>
                        <div className={'flex flex-col gap-4'}>
                            <div className={'flex flex-col gap-2'}>
                                <div className={'flex items-center justify-between gap-2'}>
                                    <Form.Label className={'text-base'} name={path('showFund')}>
                                        Display fund selection
                                    </Form.Label>
                                    <Form.Switch control={control} name={'showFund'} />
                                </div>
                                <div className={'flex flex-col'}>
                                    <p className={'text-sm text-gray-700'}>
                                        Turning this off will hide the fund selector from your form.
                                    </p>
                                </div>
                            </div>
                            {funds.length > 0 && (
                                <>
                                    <Separator />
                                    <div className={'flex flex-col gap-2'}>
                                        <Form.Container>
                                            <Form.Label
                                                className={'text-base'}
                                                name={path('defaultFund')}
                                            >
                                                Default campaign fund
                                            </Form.Label>
                                            <Form.SingleSelect control={control} name="defaultFund">
                                                <SingleSelect.Trigger aria-label="Default fund" />
                                                <SingleSelect.Content>
                                                    <SingleSelect.Item value={FUND_NONE_VALUE}>
                                                        <SingleSelect.ItemText>
                                                            None
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    {funds.map(fund => (
                                                        <SingleSelect.Item
                                                            key={fund.id}
                                                            value={fund.id.toString()}
                                                        >
                                                            <SingleSelect.ItemText>
                                                                {fund.name}
                                                            </SingleSelect.ItemText>
                                                        </SingleSelect.Item>
                                                    ))}
                                                </SingleSelect.Content>
                                            </Form.SingleSelect>
                                        </Form.Container>
                                        <div className={'flex flex-col'}>
                                            <p className={'text-sm text-gray-700'}>
                                                Select the default fund for all transactions to this
                                                campaign.
                                            </p>
                                        </div>
                                    </div>
                                </>
                            )}
                            <div>
                                <Form.SubmitButton>Save</Form.SubmitButton>
                            </div>
                        </div>
                    </form>
                </SettingsCard.RightSide>
            </SettingsCard.Root>
        </Form.Provider>
    );
};

export { CampaignToolsFundSettings };
